import { useCallback, useMemo, useRef, useEffect } from 'react';
function isValid(value, length) {
    return /^\d+$/.test(value) && value.length <= length;
}
export function mergeRefs(refs) {
    return (value) => {
        refs.forEach((ref) => {
            if (typeof ref === 'function') {
                ref(value);
            }
            else if (ref != null) {
                // eslint-disable-next-line no-param-reassign
                ref.current = value;
            }
        });
    };
}
const insertCharAt = (value, char, pos) => `${value.slice(0, pos)}${char}${value.slice(pos)}`;
const formatter = (value, { separator = '.', blocks = [2, 2, 4] } = {}) => {
    const totalLength = blocks.reduce((sum, el) => sum + el, 0);
    if (value === '') {
        return value;
    }
    if (!isValid(value, totalLength)) {
        throw Error('date autoFormatter: invalid format');
    }
    let formatted = value;
    let count = 0;
    let pos = blocks[count] + count;
    while (formatted.length >= pos) {
        formatted = insertCharAt(formatted, separator, pos);
        count += 1;
        pos += blocks[count] + count;
    }
    return formatted;
};
function stripSeparator(value) {
    return value.replace(/\D/g, '');
}
const getFormatInfo = (fmt) => {
    var _a;
    const separator = ((_a = fmt.match(/\W/)) !== null && _a !== void 0 ? _a : [])[0];
    const blocks = fmt.split(separator).map((s) => s.length);
    return { separator, blocks };
};
export const useDateAutoFormat = (value, onChange, format, enabled, forwardedRef) => {
    const ref = useRef(null);
    const pos = useRef(null);
    const inputRef = mergeRefs([ref, forwardedRef]);
    const formatInfo = useMemo(() => getFormatInfo(format), [format]);
    const setCursorPosition = useCallback(() => { var _a; return (_a = ref.current) === null || _a === void 0 ? void 0 : _a.setSelectionRange(pos.current, pos.current); }, []);
    const handleChange = (e) => {
        var _a, _b;
        const incomingValue = e.target.value;
        const isDeleting = incomingValue.length < value.toString().length;
        pos.current = (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.selectionStart) !== null && _b !== void 0 ? _b : 0;
        if (isDeleting &&
            [...value.toString()][pos.current] === formatInfo.separator) {
            setTimeout(setCursorPosition, 0);
            return;
        }
        const raw = stripSeparator(incomingValue);
        let formatted;
        try {
            formatted = formatter(raw, formatInfo);
        }
        catch (_) {
            return;
        }
        onChange(Object.assign(Object.assign({}, e), { target: Object.assign(Object.assign({}, e.target), { value: formatted }) }));
        if (!isDeleting &&
            formatted[formatted.length - 1] === formatInfo.separator) {
            pos.current += 1;
        }
    };
    useEffect(() => {
        if (enabled)
            setCursorPosition();
    }, [enabled, setCursorPosition, value]);
    if (!enabled) {
        return { value, onChange, inputRef: forwardedRef };
    }
    return { value, onChange: handleChange, inputRef };
};
